/**
 * Utility functions for formatting data.
 */
import { currencySymbol } from './currency';

/**
 * Formats a byte count into a human-readable size (KB/MB/GB, binary base).
 * @param bytes Raw byte count
 * @param decimals Fraction digits (default 1)
 * @param decimalFormat T2985: decimal separator of the organization
 *   ('comma' → "1,5 MB"); default 'dot' keeps existing callers unchanged.
 * @returns e.g. "1.5 MB", "0 B"
 */
export const formatBytes = (
  bytes: number,
  decimals = 1,
  decimalFormat: 'dot' | 'comma' = 'dot',
): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** i;
  // Bytes never need decimals.
  const fixed = i === 0 ? 0 : decimals;
  const num = value.toFixed(fixed);
  return `${decimalFormat === 'comma' ? num.replace('.', ',') : num} ${units[i]}`;
};

/**
 * Formats a number according to the project's decimal format setting.
 * @param value The number to format
 * @param format The format to use ('dot' or 'comma')
 * @param minimumFractionDigits Minimum number of fraction digits (default: 2)
 * @returns Formatted string
 */
export const formatNumber = (
  value: number | string | undefined | null,
  format: 'dot' | 'comma' = 'comma',
  minimumFractionDigits: number = 2
): string => {
  if (value === undefined || value === null) return '';

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';

  // Always use en-US (reliable grouping) then swap separators for comma format.
  // Avoids Chromium/V8 sl-SI quirk where 4-digit numbers skip the thousands separator.
  const enFormatted = num.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits: minimumFractionDigits > 0 ? minimumFractionDigits : 3,
    useGrouping: true,
  });

  if (format === 'dot') return enFormatted; // 1,234.56

  // comma (sl-SI style): "1,234.56" → "1.234,56"
  return enFormatted
    .replace(/,/g, '_')   // thousands comma → temp placeholder
    .replace(/\./g, ',')        // decimal dot → comma
    .replace(/_/g, '.');   // placeholder → dot
};

/**
 * Formats a margin/markup percentage (T1212).
 *
 * `MarginRule.margin_percent` ima na backendu 4 decimalke, ker pretvorba
 * koeficienta v pravo maržo ni končna (115 % → 13,0435 %). Prikaz s fiksnima
 * dvema decimalkama bi uporabniku pokazal "13,04 %", sistem pa bi računal z
 * drugačno vrednostjo — zato izpišemo do 4 decimalke in porežemo končne ničle:
 * "15 %" ostane "15 %", "13,0435 %" pa se izpiše v celoti.
 *
 * @param value Odstotek (string iz API-ja ali število)
 * @param format Decimalno ločilo projekta ('dot' | 'comma')
 */
export const formatPercent = (
  value: number | string | undefined | null,
  format: 'dot' | 'comma' = 'comma',
): string => {
  if (value === undefined || value === null || value === '') return '';

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';

  // toFixed(4) → odreži končne ničle ("15.0000" → "15", "13.0435" → "13.0435").
  const trimmed = num.toFixed(4).replace(/\.?0+$/, '');
  return format === 'comma' ? trimmed.replace('.', ',') : trimmed;
};

/** T2399: nedeljiv presledek (U+00A0) za `formatPercentLabel`. */
const NBSP = String.fromCharCode(0xa0);

/**
 * T2401: odstotek za PRIKAZ — `formatPercent` + (od T2399 nedeljiv) presledek pred `%`
 * (»12,5 %«, `dot` »12.5 %«), kot ga piše slovenski pravopis in kot ostali
 * zneski pišejo »699,87 €«. `formatPercent` ostaja gol, ker polni tudi vnosna
 * polja (MarginRuleEditor), kjer znak `%` ne sme biti del vrednosti.
 * Prazna/neveljavna vrednost vrne '' — nikoli golega »%«.
 */
export const formatPercentLabel = (
  value: number | string | undefined | null,
  format: 'dot' | 'comma' = 'comma',
): string => {
  const num = formatPercent(value, format);
  // T2399: nedeljiv presledek (U+00A0) — vrstica se ne sme prelomiti med
  // številko in znakom (»12,5« na koncu vrstice, »%« na začetku naslednje).
  return num === '' ? '' : `${num}${NBSP}%`;
};

/** T3146: jeziki vmesnika, ki pišejo znak % brez presledka (»75%«). */
const PERCENT_WITHOUT_SPACE = new Set(['en', 'it']);

/**
 * T3146 (Odločitev 215 b): delež za PRIKAZ v Tržnici. Število sledi decimalnemu
 * ločilu organizacije (T1675, kot zneski in ocene), zapis znaka % pa jeziku
 * vmesnika — sl/de/hr »66,7 %« z nedeljivim presledkom, en/it »66,7%«.
 * Brez vrednosti vrne pomišljaj.
 */
export const formatShare = (
  value: number | null | undefined,
  format: 'dot' | 'comma',
  language: string,
  digits = 1,
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const lang = (language || 'sl').split('-')[0].toLowerCase();
  const sep = PERCENT_WITHOUT_SPACE.has(lang) ? '' : NBSP;
  return `${formatNumber(value, format, digits)}${sep}%`;
};

// T2744: vir je `./currency` (gl. tam); izvoz naprej ohrani obstoječe uvoze.
export { currencySymbol, PROJECT_CURRENCIES, DEFAULT_PROJECT_CURRENCY } from './currency';

/**
 * T2416: EDINA funkcija za zapis zneska z valuto (2 decimalki, ločilo po
 * nastavitvi, valuta zadaj). `currency` je ISO koda ali že simbol — koda se
 * pretvori v simbol (EUR → €), zato je isti znesek povsod zapisan enako.
 * Skrajšana oblika (»253 K €«) je namenoma ločena: `formatCompactMoney`.
 */
export const formatCurrency = (
  value: number | string | undefined | null,
  currency: string = 'EUR',
  format: 'dot' | 'comma' = 'comma'
): string => {
  const formatted = formatNumber(value, format, 2);
  return `${formatted} ${currencySymbol(currency)}`;
};

/**
 * T2147: skrajšan znesek za ploščice nadzorne plošče (»12 K €«, »1,5 M €«).
 *
 * Zaledje je znesek prej oblikovalo samo (`boq_builder/dashboard.py`) z
 * valuto SPREDAJ in ločilom le iz organizacije, brez sistemskega privzetka —
 * hrvaški uporabnik je zato bral »€0.00«, preostanek aplikacije pa piše
 * »0,00 €«. Odslej oblikuje odjemalec iz surove vrednosti, z istim ločilom kot
 * ostali zneski.
 */
export const formatCompactMoney = (
  value: number | string | undefined | null,
  format: 'dot' | 'comma' = 'comma',
  symbol: string = '€',
): string => {
  if (value === undefined || value === null || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `${formatNumber(num / 1_000_000, format, 1)} M ${symbol}`;
  if (abs >= 1_000) return `${formatNumber(Math.round(num / 1_000), format, 0)} K ${symbol}`;
  return `${formatNumber(num, format, 2)} ${symbol}`;
};

/**
 * Formats a date according to the project's date format setting.
 * @param dateString The date string to format
 * @param format The format to use ('DD.MM.YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY')
 * @returns Formatted string
 */
export const formatDate = (
  dateString: string | null | undefined,
  format: 'DD.MM.YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY' = 'DD.MM.YYYY'
): string => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '';

  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear().toString();

  switch (format) {
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD.MM.YYYY':
    default:
      return `${day}.${month}.${year}`;
  }
};

const makeRelativeTimeFormat = (lang: string): Intl.RelativeTimeFormat => {
  try {
    return new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  } catch {
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  }
};

/**
 * T2034: relativni čas (»pred 2 urama«, »2 hours ago«, »vor 2 Stunden«) v
 * jeziku uporabnika — prek `Intl.RelativeTimeFormat`, zato brez prevodnih
 * ključev za vsako enoto in s pravilnimi množinskimi oblikami (sl dvojina).
 *
 * `now` je parameter samo zaradi testov. Prihodnji čas (ura odjemalca zaostaja
 * za strežnikom) se prikaže kot »zdaj«, ne kot »čez 3 sekunde«.
 */
export const formatRelativeTime = (
  dateString: string | null | undefined,
  lang: string,
  now: number = Date.now(),
): string => {
  if (!dateString) return '';
  const then = new Date(dateString).getTime();
  if (isNaN(then)) return '';

  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  const rtf = makeRelativeTimeFormat(lang);
  const MIN = 60;
  const HOUR = 60 * MIN;
  const DAY = 24 * HOUR;
  if (diffSec < MIN) return rtf.format(0, 'second');
  if (diffSec < HOUR) return rtf.format(-Math.floor(diffSec / MIN), 'minute');
  if (diffSec < DAY) return rtf.format(-Math.floor(diffSec / HOUR), 'hour');
  if (diffSec < 30 * DAY) return rtf.format(-Math.floor(diffSec / DAY), 'day');
  if (diffSec < 365 * DAY) return rtf.format(-Math.floor(diffSec / (30 * DAY)), 'month');
  return rtf.format(-Math.floor(diffSec / (365 * DAY)), 'year');
};

/**
 * T2034: točen datum in ura za `title` ob lebdenju — datum v obliki projekta
 * (`formatDate`), ura v jeziku uporabnika.
 */
export const formatDateTime = (
  dateString: string | null | undefined,
  format: 'DD.MM.YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY' = 'DD.MM.YYYY',
  lang = 'sl',
): string => {
  const date = formatDate(dateString, format);
  if (!date) return '';
  const d = new Date(dateString as string);
  let time: string;
  try {
    time = d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
  } catch {
    time = d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
  }
  return `${date} ${time}`;
};

/**
 * T2795: koliko dni je še do roka (»čez 54 dni«, »jutri«, »pred 3 dnevi«) —
 * prek `Intl.RelativeTimeFormat`, kot `formatRelativeTime`. `days` je razlika
 * v koledarskih dneh od današnjega dne (negativna = rok je zamujen), da
 * klicatelj iz nje izbere opozorilni ton. Datum se razbere v LOKALNEM času,
 * iz istega razloga kot pri `formatDeadlineDate`.
 */
export const deadlineCountdown = (
  isoDate: string | null | undefined,
  lang: string,
  now: number = Date.now(),
): { label: string; days: number } | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? '');
  if (!m) return null;
  const due = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(now);
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((due.getTime() - start.getTime()) / 86_400_000);
  return { label: makeRelativeTimeFormat(lang).format(days, 'day'), days };
};

/**
 * T2731: rok na nadzorni plošči (»pet., 23. okt.«, »Fri, Oct 23«) v jeziku
 * vmesnika — prek `Intl.DateTimeFormat`, kot `formatRelativeTime`. Zaledje je
 * oznako sestavljalo s `strftime` v C-locale vsebnika (»FRI, 23. OCT«).
 *
 * Datum brez ure (`YYYY-MM-DD`) se razbere po delih v LOKALNEM času: `new
 * Date('2026-10-23')` je polnoč UTC in bi zahodno od Greenwicha pokazal dan prej.
 */
export const formatDeadlineDate = (
  isoDate: string | null | undefined,
  lang: string,
): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate ?? '');
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const opts = { weekday: 'short', day: 'numeric', month: 'short' } as const;
  try {
    return new Intl.DateTimeFormat(lang, opts).format(d);
  } catch {
    return new Intl.DateTimeFormat('en', opts).format(d);
  }
};
